import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

export function PaymentSuccess() {
  usePageMeta({ title: "Payment | Solis Casting", noindex: true });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) return setStatus("missing");
    let ticks = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setStatus("paid");
        if (data.payment_status === "failed" || data.payment_status === "expired") return setStatus(data.payment_status);
        if (++ticks < 15) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        if (++ticks < 15) setTimeout(poll, 2000);
        else setStatus("timeout");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center px-6 sm:px-10" data-testid="payment-success-page">
      <div className="max-w-2xl">
        <p className="overline-label mb-8">Payment</p>
        {status === "paid" ? (
          <>
            <h1 className="font-display font-light text-5xl sm:text-7xl tracking-tighter mb-8" data-testid="payment-confirmed-heading">
              The call is <span className="italic text-accent">paid.</span>
            </h1>
            <p className="text-muted-foreground leading-relaxed mb-12">
              Payment confirmed. If this was a posting bundle, your plan is active now; if it was a single casting, it's in review with our office and goes live once approved.
            </p>
            <Link to="/dashboard" data-testid="payment-dashboard-link" className="btn-primary">Back to your atelier</Link>
          </>
        ) : status === "checking" ? (
          <h1 className="font-display font-light text-5xl sm:text-7xl tracking-tighter" data-testid="payment-checking-heading">
            Confirming <span className="italic text-accent">payment…</span>
          </h1>
        ) : (
          <>
            <h1 className="font-display font-light text-5xl sm:text-7xl tracking-tighter mb-8" data-testid="payment-issue-heading">
              Still <span className="italic text-accent">processing.</span>
            </h1>
            <p className="text-muted-foreground leading-relaxed mb-12">
              We couldn't confirm the payment just yet. If you were charged, it will reconcile shortly — check your dashboard in a moment.
            </p>
            <Link to="/dashboard" className="btn-outline">Back to your atelier</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  usePageMeta({ title: "Payment | Solis Casting", noindex: true });
  return (
    <div className="min-h-[70vh] flex items-center px-6 sm:px-10" data-testid="payment-cancel-page">
      <div className="max-w-2xl">
        <p className="overline-label mb-8">Payment</p>
        <h1 className="font-display font-light text-5xl sm:text-7xl tracking-tighter mb-8">
          The door stays <span className="italic text-accent">open.</span>
        </h1>
        <p className="text-muted-foreground leading-relaxed mb-12">
          Payment was cancelled — your casting is saved as a draft. Publish it whenever you're ready.
        </p>
        <Link to="/dashboard" data-testid="cancel-dashboard-link" className="btn-outline">Back to your atelier</Link>
      </div>
    </div>
  );
}
